import React, { useEffect, useRef, VoidFunctionComponent } from 'react';
import { Color, DoubleSide, MeshBasicMaterial, ShaderMaterial } from 'three';
import { useProgress, useTexture, Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { baseVertex } from '@/components/three/Base.glsl.js';
import { loaderFragment } from '@/components/three/Loader.glsl.js';
import { useResponsive } from '@/hooks/useResponsive';
import { garamondNarrowLight } from '@/lib/fonts';
import cdLoadingIcon from '@/img/cd-loading-icon.png';
import { clampedLerp } from '@/lib/mathUtils';
import botIcon from '@/img/bot-icon.png';
import { useTitlePreloader } from '@/hooks/usePreloader';

interface DreiText {
    fillOpacity: number;
    text: string;
}

const COLOR = '#FFFFFF';
const BG_SHADER_DATA = {
    uniforms: {
        loaded: { value: 100 },
        image: { value: null },
        imageAspectRatio: { value: 1 },
        imageScale: { value: 0.5 },
        ringColor: { value: new Color('#111') },
    },
    vertexShader: baseVertex,
    fragmentShader: loaderFragment,
    side: DoubleSide,
    transparent: true,
};

const CD_SCALAR = 0.3;
const FULLY_LOADED_THRESHOLD = 0.995;
const LOADING_DELAY = 1; // wait this long before starting
const LOADING_WAIT = 0.25; // hang here before after 100 before going to next scene

const SHADER_DATA = {
    uniforms: {
        loaded: { value: 0 },
        image: { value: null },
        imageAspectRatio: { value: 1 },
        imageScale: { value: 0.5 },
        ringColor: { value: new Color(COLOR) },
    },
    vertexShader: baseVertex,
    fragmentShader: loaderFragment,
    side: DoubleSide,
    transparent: true,
};

interface Props {
    onLoaded: () => void;
}

export const Loading: VoidFunctionComponent<Props> = ({ onLoaded }) => {
    useTitlePreloader();

    const materialRef = useRef() as React.MutableRefObject<ShaderMaterial>;
    const textRef = useRef<DreiText>();
    const cdIconRef = useRef<MeshBasicMaterial>(null);
    const { loaded, total } = useProgress();
    const { viewport } = useThree();
    const icon = useTexture(botIcon);
    const cdIcon = useTexture(cdLoadingIcon);
    const { width, height } = viewport.getCurrentViewport();
    const { responsiveValue } = useResponsive();
    const timer = useRef<number>(0);
    const loadingDoneTimer = useRef<number>(0);

    const cdSpacer = responsiveValue(0.1, 0);

    // Load icon image into shader
    useEffect(() => {
        if (!icon) return;
        const { width, height } = icon.source.data;
        materialRef.current.uniforms.image.value = icon;
        materialRef.current.uniforms.imageAspectRatio.value = width / height;
    }, [icon]);

    useFrame((_, delta) => {
        timer.current += delta;

        // begin loading animation after delay, proceed until approx 100%
        if (timer.current > LOADING_DELAY && materialRef.current.uniforms.loaded.value < FULLY_LOADED_THRESHOLD) {
            materialRef.current.uniforms.loaded.value = clampedLerp(
                materialRef.current.uniforms.loaded.value,
                loaded / total,
                delta / 0.25
            );

            // at 100%, quickly fade out % progress, start a timer and once we've waited a beat
            // call onLoaded
        } else if (materialRef.current.uniforms.loaded.value >= FULLY_LOADED_THRESHOLD && textRef.current) {
            textRef.current.fillOpacity = clampedLerp(textRef.current.fillOpacity || 0, 0, delta / 0.085);
            loadingDoneTimer.current += delta;
            if (loadingDoneTimer.current > LOADING_WAIT) {
                onLoaded();
            }
        }

        // update text with the loading percentage
        if (textRef.current) {
            textRef.current.text = `${Math.round(materialRef.current.uniforms.loaded.value * 100)}%`;
        }

        // random opacity for cd loader icon
        if (cdIconRef.current) {
            cdIconRef.current.opacity = Math.random() * 0.7;
        }
    });

    return (
        <>
            <group scale={responsiveValue(1.3, 1.5)}>
                <mesh>
                    <planeGeometry attach="geometry" args={[1, 1, 1]} />
                    <shaderMaterial attach="material" {...BG_SHADER_DATA} />
                </mesh>
                <mesh>
                    <planeGeometry attach="geometry" args={[1, 1, 1]} />
                    <shaderMaterial ref={materialRef} attach="material" {...SHADER_DATA} />
                </mesh>
                <Text
                    font={garamondNarrowLight}
                    fontSize={0.22}
                    color={COLOR}
                    anchorX="center"
                    position={[0, -0.7, 0]}
                    fillOpacity={1.0}
                    ref={textRef}
                >
                    0%
                </Text>
            </group>
            <mesh position={[width / 2 - CD_SCALAR - cdSpacer, -height / 2 + CD_SCALAR + cdSpacer, 0]}>
                <planeGeometry attach="geometry" args={[CD_SCALAR, CD_SCALAR]} />
                <meshBasicMaterial ref={cdIconRef} color="white" map={cdIcon} transparent opacity={0.5} />
            </mesh>
        </>
    );
};
